import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as itemService from "../../Services/Http/ItemService";
import * as orderService from "../../Services/Http/OrderService";
import * as stockRequestService from "../../Services/Http/StockRequestService";
import { useOrder } from "../../Context/useOrder";
import { Product } from "../../Models/Product";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { ScanItemResponse } from "../../Models/ScanItemResponse";
import { Button, Modal, NavLink } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { Order } from "../../Models/Order";
import { LockUnlockResponse } from "../../Models/LockUnlockResponse";
import { StockRequestPendingPick, StockRequestSaveLine } from "../../Models/StockRequestSaveRequest";
import { dataContext } from "../../Context/contextProvider";
import { observer } from "mobx-react";
import ImgWithPlaceholder from "../../Components/ImgWithPlaceholder";
import ImageCarousel from "../../Components/ImageCarousel";
import { useAuth } from "../../Context/useAuth";

//Stock Request (IRQ) screen. Scans are kept on the device and nothing goes to eSuite until Save Request:
//  - a new request is posted in one go (header, lines and picked pallets)
//  - an existing request posts its new lines, qty changes and picks through stockrequest/save
//Any scan can add an item. A pallet (serial) scan always adds its own line for the whole pallet; other scans add to the item's line.
//The bin button only takes back what was added since the request was opened: it removes a line added
//here, or undoes the scans on an existing line. Lines already in eSuite cannot be removed.

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const questionSound = new Audio();
questionSound.src = "/assets/sounds/question.mp3";
questionSound.load();

type ScanResponseStatusType = "error" | "success" | "scanbin";

//the last scan, so a qty typed straight after it can replace what it added
type LastScan = {
  detailNo?: number;
  pick: StockRequestPendingPick;
};

const scanBarcodePlaceholderMessage = "Scan pallet or product barcode";
const scanBarcodeOrQtyPlaceholderMessage = "Scan barcode or enter quantity";
const scanBinBarcodePlaceholderMessage = "Scan Bin barcode";
//tolerance for floating-point drift when fractional pack factors accumulate over repeated scans
const QTY_EPSILON = 1e-6;

const getPackFactor = (product: Product): number => {
  return (product.linePackQty || 1) * (product.linePackInnerQty || 1);
};

const formatQty = (n: number): string => n.toLocaleString(navigator.language, { maximumFractionDigits: 3 });

//an API error can be an ApiError, a string or anything else - always give the user some text
const errorText = (error: any, fallback: string = "Something went wrong"): string => {
  if (typeof error === "string" && error) {
    return error;
  }
  return (error && typeof error.message === "string" && error.message) || fallback;
};

const isBarcodeBinCode = (barcode: string): boolean => {
  const binRegex = new RegExp(/[A-Za-z][A-Za-z12]-[A-Za-z]-\d-\d\d/);
  return binRegex.test(barcode);
};

//has this line been changed on the PDA since the request was opened
const lineChanged = (l: Product): boolean =>
  !!l.isNewLine || (l.pendingPicks || []).length > 0 || Math.abs((l.qtyOrdered || 0) - (l.savedQtyOrdered || 0)) > QTY_EPSILON;

//a line added here for one scanned pallet
const isPalletLine = (l: Product): boolean => !!l.isNewLine && (l.pendingPicks || []).some((p) => !!p.serialNo);

//remember what eSuite had, so changes can be told apart and undone
const withSavedState = (order: Order): Order => ({
  ...order,
  cartLines: (order.cartLines || []).map((l) =>
    l.savedQty !== undefined ? l : { ...l, savedQty: l.qty || 0, savedQtyOrdered: l.qtyOrdered || 0, pendingPicks: [] },
  ),
});

const StockRequestPage = observer((props: Props) => {
  const { getCurrentOrder, setCurrentOrder } = useOrder();
  const navigate = useNavigate();
  const dataStore = useContext(dataContext);
  const { getSettingNumber } = useAuth();
  //a new request - built here and posted in one go when saved
  const isDraft: boolean = !!getCurrentOrder()?.isDraft;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [scanPlaceholder, setScanPlaceholder] = useState<string>(scanBarcodePlaceholderMessage);
  const [busy, setBusy] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const highlightedElementRef = useRef<HTMLElement | null>(null);
  const locked = useRef<boolean>(false);
  //first scan waiting for a bin scan
  const pendingBinScan = useRef<ScanItemResponse | null>(null);
  const lastScan = useRef<LastScan | null>(null);
  //the request as this screen last knew it. The order context only sees its own updates on the next
  //render, and a scan can add a line and then pick onto it before that happens.
  const contextOrder = getCurrentOrder();
  const orderRef = useRef<Order | null>(contextOrder);
  useEffect(() => {
    orderRef.current = contextOrder;
  }, [contextOrder]);
  const updateOrder = (order: Order) => {
    orderRef.current = order;
    setCurrentOrder(order);
  };

  useLayoutEffect(() => {
    searchBoxInputRef.current?.focus();
  }, []);

  //------------- open: remember what eSuite has, and lock an existing request while it is edited here -------------
  useEffect(() => {
    const order = getCurrentOrder();
    if (!order) {
      navigate("/stockrequests");
      return;
    }
    if (order.isDraft) {
      return;
    }
    updateOrder(withSavedState(order));
    const model = { transNo: order.transNo || "", orderCompanyNo: order.companyNo || 0 };
    orderService
      .lockOrderAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "")
      .then((resp: LockUnlockResponse) => {
        if (resp && resp.success) {
          locked.current = true;
        } else {
          toast.error("This request is locked by another user.", { autoClose: false });
          navigate("/stockrequests");
        }
      })
      .catch((error: any) => {
        toast.error(errorText(error), { autoClose: false });
      });
    return () => {
      if (locked.current) {
        locked.current = false;
        orderService.unlockOrderAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "").catch((error: any) => {
          toast.error(errorText(error), { autoClose: false });
        });
      }
    };
    // once, for the request this screen opened with
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //------------- helpers -------------
  const scrollToProduct = (detailNo?: number, highlightClass: string = "list-group-item-success") => {
    const element = document.getElementById(`stockno_${detailNo}`);
    if (element) {
      if (searchBarRef.current) {
        const stickyTopOffset = parseFloat(window.getComputedStyle(searchBarRef.current).top) || 0;
        element.style.scrollMarginTop = `${stickyTopOffset + searchBarRef.current.offsetHeight + 8}px`;
      }
      element.scrollIntoView({ behavior: "smooth" });
      element.classList.add(highlightClass);
      highlightedElementRef.current = element;
    }
    searchBoxInputRef.current?.focus();
  };

  const clearHighlight = () => {
    if (highlightedElementRef.current) {
      highlightedElementRef.current.classList.remove("list-group-item-success", "list-group-item-danger");
      highlightedElementRef.current = null;
    }
  };

  const fail = (message: string, autoClose: number | false = 2000) => {
    setSearchTerm("");
    errorSound.play();
    toast.error(message, { autoClose });
  };

  //qty (in sell packs) a scan adds - a pallet is taken whole
  const calculateAddedQty = (scan: ScanItemResponse, product: Product): number => {
    if (scan.serialNo) {
      const stockQty = Number(scan.stockQty);
      if (stockQty > 0) {
        return stockQty / getPackFactor(product);
      }
    }
    switch ((scan.scanFactor || "").toLowerCase()) {
      case "unit":
        return 1 / getPackFactor(product);
      case "inner":
        return 1 / (product.linePackQty || 1);
      default:
        return 1;
    }
  };

  const findLine = (order: Order, stockCode?: string): Product | undefined => {
    const lines = (order.cartLines || []).filter((x) => x.stockCode === stockCode);
    return lines.find((x) => (x.qtyOrdered || 0) > (x.qty || 0) + QTY_EPSILON) || lines[0];
  };

  const showScanStatus = (scan: ScanItemResponse, isSecondScan: boolean): ScanResponseStatusType => {
    setSearchTerm("");
    switch (scan?.status) {
      case 1:
        return "success";
      case 3:
        if (!isSecondScan) {
          questionSound.play();
          toast.info("Now scan Bin", { autoClose: 2000 });
          return "scanbin";
        }
        fail("Bin not found");
        return "error";
      case 2:
        fail(isSecondScan ? "Bin not found" : "Barcode not found");
        return "error";
      case 4:
        fail("Stock code not found in Bin");
        return "error";
      case 5:
        fail("Bin not found");
        return "error";
      case 6:
        fail("Bin was found with wrong stock code");
        return "error";
      default:
        fail("Error occurred");
        return "error";
    }
  };

  //------------- scanning - everything stays on the device -------------
  const applyScan = async (scan: ScanItemResponse): Promise<void> => {
    let order = orderRef.current;
    if (!order) {
      fail("Stock request not found");
      return;
    }
    //stock can only be taken from the request's From location
    if (scan.locationNo && order.locationNo && Number(scan.locationNo) !== order.locationNo) {
      fail(`That stock is not in ${order.fromLocationCode || "the From location"}`, 3000);
      return;
    }
    if (scan.serialNo && (order.cartLines || []).some((l) => (l.pendingPicks || []).some((p) => p.serialNo === scan.serialNo && p.binNo === scan.binNo))) {
      fail("That pallet has already been scanned", 3000);
      return;
    }
    //every pallet gets its own line; other scans add to the item's line, but never onto a pallet's line
    let line = scan.serialNo ? undefined : findLine({ ...order, cartLines: (order.cartLines || []).filter((l) => !isPalletLine(l)) }, scan.stockCode);
    if (!line) {
      //copy the stock details from a line already showing this item, otherwise ask eSuite for them
      const known = (order.cartLines || []).find((l) => l.stockCode === scan.stockCode);
      const item: Product = known
        ? { stockNo: known.stockNo, stockCode: known.stockCode, stockDesc: known.stockDesc, stockBarcode: known.stockBarcode, pickUnit: known.pickUnit, linePackQty: known.linePackQty, linePackInnerQty: known.linePackInnerQty, images: known.images }
        : await stockRequestService.getStockRequestItemAPI(scan.stockCode || "", dataStore.serverUrl || "", dataStore.dbKey || "");
      //lines added here get negative numbers so they never clash with eSuite's DetailNos
      const nextNewDetailNo = Math.min(0, ...(order.cartLines || []).map((l) => l.detailNo || 0)) - 1;
      line = { ...item, detailNo: nextNewDetailNo, qty: 0, qtyOrdered: 0, lineQty: 0, savedQty: 0, savedQtyOrdered: 0, pendingPicks: [], isNewLine: true, showOutstanding: true };
      order = { ...order, cartLines: [...(order.cartLines || []), line] };
    }
    addToLine(order, line, {
      serialNo: scan.serialNo,
      binNo: scan.binNo,
      binCode: scan.binCode,
      locationNo: scan.locationNo,
      pickQty: calculateAddedQty(scan, line),
    });
  };

  //adds a pick to a line, growing the requested qty when the pick takes it past what was asked for
  const addToLine = (order: Order, line: Product, pick: StockRequestPendingPick): void => {
    const picks = [...(line.pendingPicks || [])];
    const same = picks.findIndex((p) => (p.serialNo || "") === (pick.serialNo || "") && (p.binNo || "") === (pick.binNo || ""));
    if (same >= 0) {
      picks[same] = { ...picks[same], pickQty: picks[same].pickQty + pick.pickQty };
    } else {
      picks.push(pick);
    }
    const qty = (line.qty || 0) + pick.pickQty;
    const updated: Product = { ...line, qty, qtyOrdered: Math.max(line.qtyOrdered || 0, qty), pendingPicks: picks };
    updateOrder({ ...order, touched: true, cartLines: (order.cartLines || []).map((x) => (x.detailNo === line.detailNo ? updated : x)) });
    setSearchTerm("");
    //the row may be new, so let it render before scrolling to it
    setTimeout(() => scrollToProduct(updated.detailNo), 100);
    lastScan.current = { detailNo: line.detailNo, pick };
    setScanPlaceholder(scanBarcodeOrQtyPlaceholderMessage);
    successSound.play();
    toast.success(`Added ${formatQty(pick.pickQty * getPackFactor(line))}`, { autoClose: 200 });
  };

  //a small number typed after a scan replaces the qty that scan added
  const doQtyEntry = (enteredQty: number): void => {
    const order = orderRef.current;
    const last = lastScan.current;
    const line = order && last ? (order.cartLines || []).find((x) => x.detailNo === last.detailNo) : undefined;
    if (!order || !last || !line) {
      fail("Scan an item first");
      return;
    }
    const extra = enteredQty / getPackFactor(line) - last.pick.pickQty;
    if (extra <= 0) {
      fail("New quantity must be higher than the previous quantity");
      return;
    }
    addToLine(order, line, { ...last.pick, pickQty: extra });
    lastScan.current = null;
    setScanPlaceholder(scanBarcodePlaceholderMessage);
  };

  //------------- bin button - only takes back what was added since the request was opened -------------
  const canBin = (l: Product): boolean => lineChanged(l);

  const onBinLine = (e: React.MouseEvent, line: Product) => {
    e.stopPropagation();
    const order = orderRef.current;
    if (!order || !canBin(line)) {
      return;
    }
    lastScan.current = null;
    let lines = order.cartLines || [];
    if (line.isNewLine) {
      //added here - just drop it
      lines = lines.filter((l) => l.detailNo !== line.detailNo);
      toast.info(`Removed ${line.stockCode}`, { autoClose: 1000 });
    } else {
      //an eSuite line - undo what was scanned onto it this time, leaving the line as it was
      lines = lines.map((l) => (l.detailNo === line.detailNo ? { ...l, qty: l.savedQty || 0, qtyOrdered: l.savedQtyOrdered || 0, pendingPicks: [] } : l));
      toast.info(`Undid the scans on ${line.stockCode}`, { autoClose: 1000 });
    }
    updateOrder({ ...order, cartLines: lines });
    searchBoxInputRef.current?.focus();
  };

  //------------- save / discard -------------
  const hasChanges = (order: Order | null): boolean => {
    if (!order) {
      return false;
    }
    return order.isDraft ? (order.cartLines || []).some((l) => (l.qtyOrdered || 0) > 0) : (order.cartLines || []).some(lineChanged);
  };

  const onSave = async (): Promise<void> => {
    const order = orderRef.current;
    if (!order || !hasChanges(order)) {
      return;
    }
    setSaving(true);
    try {
      let savedRef = order.transRefCode;
      if (order.isDraft) {
        const created = await stockRequestService.createStockRequestAPI(
          {
            fromLocationNo: order.locationNo || 0,
            toLocationNo: order.originLocationNo || 0,
            description: order.transComment || "",
            lines: (order.cartLines || [])
              .filter((l) => (l.qtyOrdered || 0) > 0)
              .map((l) => ({
                stockCode: l.stockCode || "",
                lineQty: l.qtyOrdered || 0,
                pickQty: l.qty || 0,
                serials: (l.pendingPicks || [])
                  .filter((p) => !!p.serialNo)
                  .map((p) => ({ serialNo: p.serialNo!, binNo: p.binNo, binCode: p.binCode, locationNo: p.locationNo, serialQty: p.pickQty * getPackFactor(l) })),
              })),
          },
          dataStore.serverUrl || "",
          dataStore.dbKey || "",
        );
        savedRef = created?.transRefCode;
      } else {
        const lines: StockRequestSaveLine[] = (order.cartLines || []).filter(lineChanged).map((l) => {
          const qtyChanged = Math.abs((l.qtyOrdered || 0) - (l.savedQtyOrdered || 0)) > QTY_EPSILON;
          return {
            detailNo: l.isNewLine ? 0 : l.detailNo || 0,
            stockCode: l.stockCode || "",
            //the requested qty grows by however much the outstanding qty grew
            lineQty: l.isNewLine ? l.qtyOrdered || 0 : qtyChanged ? (l.lineQty || l.savedQtyOrdered || 0) + ((l.qtyOrdered || 0) - (l.savedQtyOrdered || 0)) : undefined,
            picks: l.pendingPicks || [],
          };
        });
        await stockRequestService.saveStockRequestAPI(
          { transRefCode: order.transRefCode || "", companyNo: order.companyNo || 0, transNo: parseInt(order.transNo || "0", 10), lines },
          dataStore.serverUrl || "",
          dataStore.dbKey || "",
        );
      }
      successSound.play();
      toast.success(`Stock request ${savedRef || ""} saved`, { autoClose: 2000 });
      setCurrentOrder(null);
      navigate("/stockrequests");
    } catch (error: any) {
      fail(errorText(error, "Saving the request failed"), false);
    } finally {
      setSaving(false);
    }
  };

  const onDiscard = () => {
    if (hasChanges(orderRef.current) && !window.confirm("Discard your changes? Nothing has been saved to eSuite.")) {
      return;
    }
    setCurrentOrder(null);
    navigate("/stockrequests");
  };

  //looks a scan up for this request:
  //  1. as a pallet serial in the From location - serials are unique, so this is never ambiguous
  //  2. as a barcode on the request's own lines (existing request), like the Pick screen
  //  3. as a barcode across all stock, so new items can be added
  //Product barcodes can be shared, which makes pda_BarcodeScan refuse with "Duplicated barcode" in 3.
  const scanBarcode = async (order: Order, barcode: string): Promise<ScanItemResponse> => {
    try {
      const serial = await stockRequestService.scanStockRequestSerialAPI(barcode, order.locationNo, dataStore.serverUrl || "", dataStore.dbKey || "");
      if (serial && serial.status === 1) {
        return serial;
      }
    } catch {
      //serial lookup unavailable (e.g. pda_SerialScan not installed) - carry on with the barcode lookup
    }
    const lookup = (transNo?: string) =>
      itemService.scanItemFirstAPI(barcode, dataStore.serverUrl || "", dataStore.dbKey || "", order.companyNo, transNo, true);
    try {
      if (!order.isDraft && order.transNo) {
        const onRequest = await lookup(order.transNo);
        //2 = barcode not found among the request's lines
        if (onRequest && onRequest.status !== 2) {
          return onRequest;
        }
      }
      return await lookup(undefined);
    } catch (error: any) {
      if (/duplicated barcode/i.test(errorText(error, ""))) {
        throw new Error("That barcode matches more than one item or pallet - scan the pallet label or the item's own barcode");
      }
      throw error;
    }
  };

  //------------- keyboard / scanner -------------
  const getBarcodeQty = (barcode: string): number => {
    const trimmed = (barcode || "").trim();
    if (!trimmed || isNaN(Number(trimmed))) {
      return 0;
    }
    let digits = getSettingNumber(dataStore.serverUrl!, dataStore.dbKey!, "PDAQtyDigits");
    if (digits <= 0) {
      digits = 2;
    }
    return trimmed.length <= digits ? parseInt(trimmed, 10) : 0;
  };

  const onBarcodeScanned = useCallback(
    async (barcode: string): Promise<void> => {
      if (!barcode || busy || saving) {
        return;
      }
      clearHighlight();
      const order = orderRef.current;
      if (!order) {
        fail("Stock request not found");
        return;
      }
      setBusy(true);
      try {
        //second half of a bin scan
        if (pendingBinScan.current && isBarcodeBinCode(barcode)) {
          const first = pendingBinScan.current;
          pendingBinScan.current = null;
          setScanPlaceholder(scanBarcodePlaceholderMessage);
          const scan = await itemService.scanItemSecondAPI(barcode, first.stockCode || "", dataStore.serverUrl || "", dataStore.dbKey || "", order.companyNo);
          if (showScanStatus(scan, true) === "success") {
            await applyScan({ ...first, ...scan });
          }
          return;
        }

        const enteredQty = lastScan.current ? getBarcodeQty(barcode) : 0;
        if (enteredQty > 0) {
          doQtyEntry(enteredQty);
          return;
        }

        lastScan.current = null;
        const scan = await scanBarcode(order, barcode);
        const status = showScanStatus(scan, false);
        if (status === "scanbin") {
          pendingBinScan.current = scan;
          setScanPlaceholder(scanBinBarcodePlaceholderMessage);
        } else if (status === "success") {
          setScanPlaceholder(scanBarcodePlaceholderMessage);
          await applyScan(scan);
        }
      } catch (error: any) {
        fail(errorText(error), false);
      } finally {
        setBusy(false);
      }
    },
    // the handlers read the latest order through orderRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy, saving],
  );

  const onKeyPressed = useCallback(
    (event: any) => {
      if (event.target && event.target instanceof HTMLInputElement && event.target.name === searchBoxInputRef.current?.name) {
        return;
      }
      const keyPressed = event.key ? event.key : "";
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          onBarcodeScanned(oldState);
          return oldState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => oldState + keyPressed);
      }
    },
    [onBarcodeScanned],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => {
      window.removeEventListener("keydown", onKeyPressed);
    };
  }, [onKeyPressed]);

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onBarcodeScanned(searchTerm);
    }
  };

  const getPickedCssClassName = (product: Product): string => {
    return `qty-badge badge ${(product.qty || 0) + QTY_EPSILON >= (product.qtyOrdered || 0) ? "bg-success" : "bg-secondary"}`;
  };

  const order = contextOrder;
  const changed = hasChanges(order);
  return (
    <>
      {order && (
        <div className="row sticky-top-2">
          <div className="col-12 my-2">
            <div className="container-limited-width d-flex justify-content-between align-items-center">
              <div style={{ fontSize: "1.4rem" }}>
                <b>{isDraft ? "New Request" : order.transRefCode}</b>
              </div>
              <span className="bg-info p-1 px-2 rounded text-nowrap fw-bold">
                {order.fromLocationCode} <i className="fa fa-long-arrow-right mx-1" aria-hidden="true"></i> {order.toLocationCode}
              </span>
            </div>
            {order.transComment && <div className="container-limited-width text-muted small">{order.transComment}</div>}
          </div>
        </div>
      )}
      <div className="container-limited-width">
        <div className="row sticky-top-3 search-bar" ref={searchBarRef}>
          <div className="col-12 p-2">
            <input
              name="searchBox"
              type="text"
              className="form-control search-box"
              placeholder={scanPlaceholder}
              autoCapitalize="none"
              autoComplete="off"
              onKeyUp={handleKeyUp}
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
              disabled={busy || saving}
              ref={searchBoxInputRef}
            />
          </div>
        </div>
        {order && (order.cartLines || []).length === 0 && (
          <div className="text-muted text-center my-4">No items yet - scan a pallet or product to add it to the request.</div>
        )}
        <div className="row">
          <div className="col-12 mb-2 mt-2 p-0">
            <ul className="list-group list-group-flush p-0">
              {order &&
                (order.cartLines || []).map((product, index) => {
                  return (
                    <li
                      id={`stockno_${product.detailNo}`}
                      key={`${product.detailNo}_${index}`}
                      className={`list-group-item d-flex align-items-start p-1 product-list-item`}
                      onClick={() => {
                        if (product.images && product.images.length > 0) {
                          setSelectedProduct(product);
                        }
                      }}
                    >
                      <div className="d-flex flex-column product-image-container">
                        {product.images && product.images.length > 0 ? (
                          <ImgWithPlaceholder
                            src={product.images[0].thumbImageUrl}
                            alt={product.stockDesc}
                            className="product-image"
                            fallback={<img src="/assets/images/no-image-100x100.png" alt="" className="rounded product-image" />}
                          />
                        ) : null}
                        <div className="d-flex align-self-center align-items-center my-1">
                          <span className={getPickedCssClassName(product)}>
                            {formatQty((product.qty || 0) * getPackFactor(product))}
                            &nbsp;/&nbsp;
                            {formatQty((product.qtyOrdered || 0) * getPackFactor(product))}
                          </span>
                          <div className="d-flex pickunit align-items-center">{product.pickUnit}</div>
                        </div>
                      </div>
                      <div className="d-flex flex-column flex-grow-1 mx-2">
                        <div className="fw-bold">
                          {product.stockDesc}
                          {product.isNewLine && !isDraft && <span className="badge bg-info ms-2">New</span>}
                          {!product.isNewLine && lineChanged(product) && <span className="badge bg-warning text-dark ms-2">Changed</span>}
                        </div>
                        <div className="text-muted">{product.stockCode}</div>
                        <div className="text-muted">Barcode: {product.stockBarcode}</div>
                      </div>
                      <div className="d-flex align-items-start">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={busy || saving || !canBin(product)}
                          onClick={(e) => onBinLine(e, product)}
                          title={product.isNewLine ? "Remove this item" : "Undo the scans on this item"}
                        >
                          <BsTrash />
                        </Button>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      </div>
      <Modal show={!!selectedProduct} onHide={() => setSelectedProduct(undefined)} keyboard={true} fullscreen="xxl-down" centered>
        <Modal.Header closeButton={true}>
          <Modal.Title>{selectedProduct?.stockDesc}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProduct && selectedProduct.images && selectedProduct.images.length > 0 && (
            <ImageCarousel imageUrls={selectedProduct.images.filter((i) => i !== undefined && i.largeImageUrl !== undefined).map((i) => i.largeImageUrl!)} alt={selectedProduct.stockDesc} />
          )}
          {selectedProduct && (
            <div className="d-flex flex-column fw-bold mt-3">
              <div className="text-muted">StockCode: {selectedProduct.stockCode}</div>
              <div className="text-muted">Barcode: {selectedProduct.stockBarcode}</div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      <footer className="footer footer-pinned mt-5 fixed-bottom">
        <div className="container-fluid d-flex justify-content-around bottom-nav pt-2">
          <NavLink className="d-flex flex-column" title="Discard" onClick={onDiscard} disabled={saving}>
            <i className="fa fa-times footer-icon" aria-hidden="true"></i>
            <span>Discard</span>
          </NavLink>
          <NavLink className="d-flex flex-column" title="Save Request" onClick={onSave} disabled={saving || busy || !changed}>
            <i className="fa fa-check footer-icon" aria-hidden="true"></i>
            <span>{saving ? "Saving..." : "Save Request"}</span>
          </NavLink>
        </div>
      </footer>
    </>
  );
});

export default StockRequestPage;
